"use client"

import type { Testimonial } from "@/lib/cms"
import { useTranslations } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BloodMembershipComparison } from "@/components/ui/blood-membership-comparison"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { TestimonialsSection } from "../../../_components/testimonials-section"
import {
  BiomarkerHeroSection,
  BiomarkerDetailCardSection,
  BiomarkerFactsSection,
  BiomarkerGapSection,
  BiomarkerRisksSection,
  BiomarkerActionSection,
  BiomarkerFaqSection,
  BiomarkerClosingSection,
  LinkedCopy,
} from "@/components/ui/biomarker-landing"
import { BiomarkerStepsWalkthrough } from "@/components/ui/biomarker-steps-walkthrough"
import { psaConfig } from "./psa-config"

export default function PsaClient({
  cmsTestimonials,
  cmsFaqs,
  priceFrom,
}: {
  cmsTestimonials: Testimonial[]
  cmsFaqs: { q: string; a: string }[]
  priceFrom?: string
}) {
  const lp = useLocalePath()
  const t = useTranslations("go")
  const config = psaConfig(
    t,
    lp("/what-we-detect"),
    lp("/tests/advanced-blood-test"),
    priceFrom
  )
  const { biomarkerPanel } = config
  // Prefer CMS FAQ; fall back to the prototype set until FAQs are authored for this page.
  if (cmsFaqs.length) config.faq.items = cmsFaqs

  return (
    <>
      <BiomarkerHeroSection config={config} />
      <BiomarkerDetailCardSection config={config} />
      <BiomarkerFactsSection config={config} />
      <BiomarkerRisksSection config={config} />
      <BiomarkerGapSection config={config} />
      <BiomarkerActionSection config={config} />
      <BiomarkerStepsWalkthrough config={config} />
      {/* Biomarker panel (FH-style) — makes "part of the blood test" concrete */}
      <section className="scroll-mt-24 bg-neutral-50 py-12 md:py-16">
        <BiomarkerDiseaseExplorer
          packages={["blood", "addon-single"]}
          // Payload doc id for PSA (locale-invariant). Do NOT use the slug — it
          // is a localized field and can differ per locale.
          addonIds={["47"]}
          tabsAllowed={["biomarkers"]}
          diseaseContentEnabled={false}
          isBloodTest
          eyebrow={biomarkerPanel.eyebrow}
          headingSizeClass="type-display-sm"
          biomarkersTab={{
            heading: biomarkerPanel.heading,
            subheading: (
              <LinkedCopy
                text={biomarkerPanel.subheading}
                phrase="Advanced blood test"
                href={config.advancedBloodTestHref}
              />
            ),
            cta: {
              label: biomarkerPanel.ctaLabel,
              href: lp("/biomarkers"),
              variant: "text",
            },
          }}
        />
      </section>
      {/* Real social proof (CMS testimonials) */}
      <TestimonialsSection
        eyebrow={t("psaTestimonialsEyebrow")}
        heading={t("psaTestimonialsHeading")}
        headingClassName="type-display-sm text-balance tracking-tight text-center text-neutral-900"
        cmsTestimonials={cmsTestimonials}
      />
      {/* Pricing — add-on framing: Advanced Blood Test (with Lp(a)) vs membership */}
      <div className="py-8 md:py-12">
        <BloodMembershipComparison />
      </div>
      <BiomarkerFaqSection config={config} />
      <BiomarkerClosingSection config={config} />
    </>
  )
}
